import type { ExpressionStudy } from "./expression-studies";

export const EXPRESSION_STUDIO_WAVE_01: ExpressionStudy[] = [
  {
    id: "finish-before-the-answer",
    contentVersion: 1,
    number: "13",
    title: "Finish before the answer",
    provenance: "Original Verbalore",
    craft: ["upward boundary", "evidence-first", "composed interruption"],
    size: "quick",
    minutes: 4,
    context: "A client risk review with a senior director",
    skill: "Protecting the end of an answer when the interrupter has more authority",
    route: { intention: "hold-ground", relationship: "person-you-need-to-move", risk: "hostility" },
    landingHazard: {
      label: "An upward boundary can sound like a contest for status",
      accidentalReading: "If you stress the interruption rather than the missing evidence, the director may hear a public challenge instead of a request to complete the answer.",
      deliveryCue: "Keep your eyes on the work, not the interruption. Name the evidence you still need to finish and hand the turn back when you are done.",
      saferAlternative: "Let me finish the migration point, then I want your view on it.",
    },
    situation:
      "In a client risk review, your director interrupts twice before you can explain that the estimate excludes migration testing. You need ninety seconds to finish the evidence before the room decides. You respect their authority and do not need their permission to complete the answer.",
    tangledDraft:
      "Sorry, I know you have more experience, but if it’s okay, I really need to finish this.",
    draftDiagnosis:
      "The sentence is respectful, but it makes seniority the subject and asks permission for an answer the room already requested. Keep the respect while making the unfinished evidence, not the status difference, the reason to continue.",
    intention: "Finish the migration-risk evidence before the director or room responds.",
    invariant: "You need ninety seconds to complete the missing migration evidence. The director receives the next turn and their authority is not challenged.",
    concept: "An upward boundary is strongest when it protects the decision, not the speaker’s pride.",
    practiceCue: "Name the unfinished evidence, protect the next turn, and remove every apology for completing the answer.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "I need ninety seconds to finish the migration-risk evidence before we decide.",
        landing: "Firm and work-centred",
        protects: "The decision receives the missing evidence before the room moves on.",
        tradeoff: "I need can sound like a public demand if your delivery becomes personal.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "Let me finish the migration point, then I want your view on it.",
        landing: "Respectful sequence",
        protects: "Your answer and the director’s response both retain a clear place.",
        tradeoff: "The invitation to respond can soften the urgency of the missing evidence.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "Give me ninety seconds to land the evidence, then I’m with you.",
        landing: "Human and assured",
        protects: "Connection survives while the order of turns stays clear.",
        tradeoff: "The conversational warmth may feel too casual in a formal client room.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "The migration evidence is not complete yet. I’ll finish it first.",
        landing: "Cool and factual",
        protects: "The unfinished work, rather than the interruption, carries the boundary.",
        tradeoff: "The director’s next turn remains implied rather than explicitly welcomed.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Ninety seconds, then we decide with the full risk in view.",
        landing: "Compressed and consequential",
        protects: "The time request and reason arrive in one memorable line.",
        tradeoff: "The compression can sound like you are instructing the director how the room will proceed.",
      },
    ],
  },
  {
    id: "correct-the-confident-error",
    contentVersion: 1,
    number: "14",
    title: "Correct the confident error",
    provenance: "Original Verbalore",
    craft: ["evidential correction", "face-saving", "public precision"],
    size: "standard",
    minutes: 8,
    context: "A budget meeting where your manager states the wrong pilot result",
    skill: "Correcting a confident factual error without making the correction a verdict on the person",
    route: { intention: "correct-with-care", relationship: "group", risk: "humiliation" },
    landingHazard: {
      label: "Accuracy can become a public victory over someone",
      accidentalReading: "A triumphant tone, a long pause before the number, or emphasis on you can make a necessary correction feel like exposure.",
      deliveryCue: "Put the dashboard and the decision in the foreground. Correct the number once, then move immediately to verification or action.",
      saferAlternative: "The current dashboard shows 62 percent. Can we verify the figure before we decide?",
    },
    situation:
      "In a budget meeting, your manager says the pilot reached 80 percent adoption. The live dashboard shows 62 percent, and the room is about to use the number to approve expansion. You need to correct the figure without suggesting that your manager lied or is incompetent.",
    tangledDraft:
      "I could be wrong, but I thought it might be closer to sixty percent?",
    draftDiagnosis:
      "The sentence protects your manager by making verified evidence sound like a vague memory. That may let the wrong number govern the decision. Name the source, state the figure, and leave room to check it.",
    intention: "Replace the incorrect 80 percent figure with the dashboard’s 62 percent before the budget decision.",
    invariant: "The room must not decide from 80 percent when the current dashboard shows 62 percent. The correction does not accuse the manager of dishonesty or incompetence.",
    concept: "Correction serves the shared decision when evidence, not embarrassment, occupies the centre of the sentence.",
    practiceCue: "State the source and number in one breath. Remove language that either gloats or makes the evidence optional.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "The current dashboard shows 62 percent, not 80. We should use 62 for this decision.",
        landing: "Clear evidential correction",
        protects: "The room receives the accurate figure and its consequence immediately.",
        tradeoff: "Not 80 can feel publicly exposing even when the correction is necessary.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I may be looking at a different cut. The current dashboard shows 62 percent; can we verify before we decide?",
        landing: "Corrective without accusation",
        protects: "The accurate figure enters while allowing for a source mismatch.",
        tradeoff: "The opening qualification may suggest less confidence than the dashboard warrants.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "Let’s pause on the number. The dashboard I checked this morning shows 62 percent, and I want us deciding from the same evidence.",
        landing: "Shared standard",
        protects: "The correction becomes a collective accuracy check rather than a personal contest.",
        tradeoff: "The longer framing delays the corrected figure.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "The current dashboard reads 62 percent.",
        landing: "Quiet factual marker",
        protects: "The evidence is stated without commentary on how the error arose.",
        tradeoff: "It does not explicitly stop the room from continuing with the wrong decision.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Sixty-two, not eighty. Let’s verify before we commit.",
        landing: "Fast and unmistakable",
        protects: "The corrected figure and required pause are hard to miss.",
        tradeoff: "The contrast can sound like a public takedown if the room is already tense.",
      },
    ],
  },
  {
    id: "buy-one-more-day",
    contentVersion: 1,
    number: "15",
    title: "Buy one more day",
    provenance: "Original Verbalore",
    craft: ["deadline negotiation", "risk translation", "upward ask"],
    size: "standard",
    minutes: 7,
    context: "A one-to-one deadline negotiation with your manager",
    skill: "Asking upward for a later deadline by making the exchanged value explicit",
    route: { intention: "ask-or-persuade", relationship: "person-you-need-to-move", risk: "vagueness" },
    landingHazard: {
      label: "A deadline request can sound like delay without value",
      accidentalReading: "If the sentence names only your need for time, the manager may hear avoidance rather than a choice between an unverified Friday release and a verified Monday release.",
      deliveryCue: "Lead with the exact date you are requesting and tie the extra time to the completed security rerun.",
      saferAlternative: "Can we move the deadline to Monday? That includes the completed security rerun instead of shipping without it.",
    },
    situation:
      "Your manager asks for a Friday release. The final security rerun completes on Saturday, so Friday would mean shipping without that evidence. You can deliver a verified release on Monday. You need to ask for the later date without pretending the trade-off does not exist.",
    tangledDraft:
      "Friday is probably fine, though Monday might be safer if that’s possible.",
    draftDiagnosis:
      "The sentence conceals both the ask and the risk. Probably fine invites Friday while safer if possible makes Monday sound like a preference. State the date you need and what the extra day buys.",
    intention: "Move the deadline from Friday to Monday so the release includes the completed security rerun.",
    invariant: "You are asking for Monday. Friday would ship without the final security evidence; Monday includes it. Your manager still decides whether to accept that trade-off.",
    concept: "A good deadline negotiation does not ask for time in the abstract. It names what the time purchases.",
    practiceCue: "Put the requested date first, then name the evidence or quality that becomes possible because of it.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "I’m asking to move the deadline to Monday. The security rerun finishes Saturday, and Friday would mean shipping without it.",
        landing: "Explicit ask and trade-off",
        protects: "The manager can decide from the real difference between the dates.",
        tradeoff: "The sentence gives little reassurance about the rest of the release.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "Can we move the deadline to Monday? That gives us the completed security rerun instead of asking you to accept unverified risk.",
        landing: "Respectful and concrete",
        protects: "Authority remains with the manager while the cost of Friday stays visible.",
        tradeoff: "Asking you to accept may sound as if refusal would be irresponsible.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "Give the work one more day and I can bring you a verified release on Monday, not a hopeful one on Friday.",
        landing: "Confident value exchange",
        protects: "The request is carried by pride in the finished work.",
        tradeoff: "Hopeful one can sound dismissive of legitimate pressure to release Friday.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "Monday is the first deadline that includes the completed security rerun.",
        landing: "Factual and controlled",
        protects: "The timing evidence speaks without dramatizing the request.",
        tradeoff: "The ask itself remains implied and may not receive a direct answer.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Friday is fast. Monday is verified. I’m asking for Monday.",
        landing: "Memorable contrast",
        protects: "The decision and its quality difference are impossible to miss.",
        tradeoff: "The contrast oversimplifies other reasons the Friday date may matter.",
      },
    ],
  },
  {
    id: "standards-without-threat",
    contentVersion: 1,
    number: "16",
    title: "Standards without threat",
    provenance: "Original Verbalore",
    craft: ["leadership", "behavioural standard", "support with accountability"],
    size: "deep",
    minutes: 11,
    context: "Private feedback from a team lead to an engineer",
    skill: "Setting a firm standard while leaving room to surface the obstacle",
    route: { intention: "correct-with-care", relationship: "person-you-lead", risk: "humiliation" },
    landingHazard: {
      label: "A standard can arrive as a threat to belonging",
      accidentalReading: "If consequences or rank dominate the sentence, the engineer may hear that one more miss makes them disposable rather than hearing the required behaviour and available support.",
      deliveryCue: "Name the repeated behaviour, state the rule, and offer help with the obstacle. Do not speculate about motive or character.",
      saferAlternative: "From now on, please get peer review before merging, and tell me early if coverage is the obstacle.",
    },
    situation:
      "You lead an engineering team. One engineer has merged changes without peer review twice in three weeks. No defect reached customers, but the skipped check creates avoidable risk. In a private conversation, you need to make peer review mandatory from now on and invite them to raise any coverage problem early.",
    tangledDraft:
      "It would be great if we could try to be more consistent about review.",
    draftDiagnosis:
      "The wording is pleasant but turns a repeated breach into a general aspiration. The engineer cannot tell what must change, when it changes, or what to do if the process is blocked.",
    intention: "Require peer review before every future merge and make coverage problems discussable before the rule is broken.",
    invariant: "From now on, no change merges without peer review. The engineer can ask for help finding coverage, and the feedback does not attack their competence or motive.",
    concept: "Authority is clearest when the standard and the support are both real. Threat is not a substitute for either.",
    practiceCue: "Name the behaviour, the rule, and the route for a genuine obstacle. Remove character judgments and vague hopes.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "Starting today, every change needs peer review before it merges. If coverage is the obstacle, bring it to me.",
        landing: "Firm rule with a route",
        protects: "The standard is unmistakable and an operational problem can still be raised.",
        tradeoff: "The opening can feel disciplinary even in a private conversation.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "We’ve skipped review twice. From now on, please get peer review before merging, and tell me early if you cannot find coverage.",
        landing: "Specific and fair",
        protects: "The feedback stays attached to observed behaviour and a clear next action.",
        tradeoff: "Please may make the mandatory rule sound negotiable if your delivery is uncertain.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "The work deserves a second pair of eyes, and so do you. From now on, every change gets peer review before merge; I’ll help remove any coverage block.",
        landing: "Protective and firm",
        protects: "The engineer receives both the standard and evidence that support is genuine.",
        tradeoff: "The first sentence may feel paternal if the relationship lacks trust.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "No change merges without peer review from today.",
        landing: "Controlled rule",
        protects: "The standard cannot be mistaken for a suggestion.",
        tradeoff: "The obstacle and support path disappear, so a process problem may remain hidden.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Review first. Merge second.",
        landing: "Memorable order",
        protects: "The required sequence is easy to remember.",
        tradeoff: "As private feedback it can sound like a slogan used to end discussion rather than solve the repeated problem.",
      },
    ],
  },
  {
    id: "jealousy-without-ownership",
    contentVersion: 1,
    number: "17",
    title: "Jealousy without ownership",
    provenance: "Original Verbalore",
    craft: ["emotional precision", "non-accusation", "intimate agency"],
    size: "deep",
    minutes: 12,
    context: "A conversation with a partner after a difficult surprise",
    skill: "Naming jealousy as your feeling without turning it into control over another adult",
    route: { intention: "say-a-hard-thing", relationship: "someone-close", risk: "coercion" },
    landingHazard: {
      label: "Jealousy can disguise a command as a confession",
      accidentalReading: "A sentence that begins with your feeling may still imply that your partner must cancel, confess, or prove innocence in order to manage it for you.",
      deliveryCue: "Own the feeling in the first person. Separate the request for conversation from any decision your partner remains free to make.",
      saferAlternative: "I felt jealous hearing about the concert after the ticket was bought. I’d like us to talk about it.",
    },
    situation:
      "Your partner accepted a concert invitation from a former partner and told you after the ticket was bought. You feel jealous and unsettled. You are not accusing them of infidelity or asking them to cancel, but you do need an honest conversation about why the decision was made before you knew.",
    tangledDraft:
      "I’m fine. Go if you want. Obviously it means nothing.",
    draftDiagnosis:
      "The sentence denies the feeling while using sarcasm to punish the listener for not noticing it. It also makes go if you want sound like permission that can later be withdrawn. Name the jealousy and ask for the conversation you actually need.",
    intention: "Name your jealousy and ask for a conversation about the late disclosure without accusation or control.",
    invariant: "You feel jealous because you learned after the ticket was bought. You want a real conversation. You are not alleging betrayal, ordering cancellation, or claiming ownership of your partner’s choices.",
    concept: "A feeling becomes discussable when it is owned precisely. It becomes control when another person is made responsible for erasing it.",
    practiceCue: "Use I for the feeling, name the event that stirred it, and make one request that leaves the other adult’s agency intact.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "I’m jealous, and I don’t want to hide it or turn it into an accusation. Can we talk about why I heard after the ticket was bought?",
        landing: "Exposed and accountable",
        protects: "The feeling and its cause are clear without inventing wrongdoing.",
        tradeoff: "The explicit denial of accusation may make the listener brace for one.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I trust you, and I still felt jealous hearing about the concert after the ticket was bought. I’d like us to talk about it.",
        landing: "Trust beside discomfort",
        protects: "Trust is preserved while the difficult feeling remains fully visible.",
        tradeoff: "Leading with trust can sound like reassurance you are not yet ready to give.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "I care about us enough to say the awkward thing: I’m jealous. I’m not asking you to cancel; I am asking for a real conversation.",
        landing: "Intimate and self-possessed",
        protects: "Care, agency, and the request for honesty stay in the same frame.",
        tradeoff: "The careful structure can feel rehearsed in a raw moment.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "I felt jealous when I heard after the decision was made. I want to understand it before we move on.",
        landing: "Quietly vulnerable",
        protects: "The feeling is named without spectacle or a demand to cancel.",
        tradeoff: "Decision was made can conceal that the ticket, not the concert itself, created the injury.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "I’m jealous, not your jailer. Talk to me.",
        landing: "Memorable and exposed",
        protects: "The line rejects control while refusing to hide the feeling.",
        tradeoff: "The rhyme and command can feel performative or coercive in the very moment it claims not to control.",
      },
    ],
  },
  {
    id: "ask-for-another-date",
    contentVersion: 1,
    number: "18",
    title: "Ask for another date",
    provenance: "Original Verbalore",
    craft: ["romantic candour", "freedom to refuse", "unforced warmth"],
    size: "standard",
    minutes: 7,
    context: "A message after a first date",
    skill: "Expressing romantic interest without making the other person manage your uncertainty",
    route: { intention: "ask-or-persuade", relationship: "new-connection", risk: "coercion" },
    landingHazard: {
      label: "Charm can make a simple invitation feel like pressure",
      accidentalReading: "A grand declaration, a flood of praise, or a line that assumes a shared future can make the other person feel they must reject more than one date.",
      deliveryCue: "Name your enjoyment, make one concrete invitation, and leave space for an uncomplicated no.",
      saferAlternative: "I enjoyed last night. Would you like to meet again next Thursday?",
    },
    situation:
      "The morning after a first date, you know you would like a second one. You want to say so clearly and suggest next Thursday. The connection is new, and the other person must remain free to decline without comforting you or answering a larger declaration.",
    tangledDraft:
      "Last night was fun, unless you didn’t think so. No worries either way, obviously.",
    draftDiagnosis:
      "The interest is buried under an imagined rejection and there is no invitation to answer. The other person has to reassure you before they can decide whether they want another date.",
    intention: "Say you enjoyed the first date and ask whether the other person wants to meet again next Thursday.",
    invariant: "You want a second date and are asking for next Thursday. The other person remains free to decline without managing your disappointment.",
    concept: "Romantic confidence is not certainty of being wanted. It is the ability to reveal interest without converting hope into pressure.",
    practiceCue: "Name one true pleasure, make one answerable invitation, and remove every sentence that asks for reassurance before the answer.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "I enjoyed last night and I’d like to see you again. Are you free next Thursday?",
        landing: "Open and answerable",
        protects: "Your interest and the concrete invitation are both clear.",
        tradeoff: "The directness leaves little room to test the atmosphere before asking.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I had a lovely time. If you’d like another date too, are you free next Thursday?",
        landing: "Interested with room",
        protects: "The invitation visibly depends on mutual interest.",
        tradeoff: "If you’d like can sound more tentative than the connection requires.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "I’ve been smiling about last night. I’d like a second date, if you would.",
        landing: "Warmly exposed",
        protects: "Pleasure and desire are admitted without pretending they are shared.",
        tradeoff: "It does not name a date, so the invitation is less easy to answer.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "I enjoyed last night. Would you like to meet again next Thursday?",
        landing: "Simple and poised",
        protects: "The interest is sufficient and the invitation remains light.",
        tradeoff: "The restraint may understate strong chemistry you would happily reveal.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Last night deserves a sequel. Thursday?",
        landing: "Playful and bold",
        protects: "The invitation is memorable and easy to answer.",
        tradeoff: "The cleverness can feel rehearsed or assume that the first date was mutually successful.",
      },
    ],
  },
  {
    id: "receive-the-compliment",
    contentVersion: 1,
    number: "19",
    title: "Receive the compliment",
    provenance: "Original Verbalore",
    craft: ["social poise", "earned credit", "gracious receipt"],
    size: "quick",
    minutes: 3,
    context: "A colleague compliments a difficult presentation",
    skill: "Accepting praise without erasing the work or turning the reply into a boast",
    route: { intention: "answer-pressure", relationship: "work-peer", risk: "self-erasure" },
    landingHazard: {
      label: "Modesty can reject the person who offered the praise",
      accidentalReading: "Calling the work nothing or saying anyone could have done it can make a sincere compliment feel foolish while erasing your own effort.",
      deliveryCue: "Say thank you before explaining anything. Add one truthful sentence about the work only if it helps you receive rather than deflect.",
      saferAlternative: "Thank you. I worked hard to make it clear.",
    },
    situation:
      "After a difficult presentation, a colleague tells you, ‘You made that complicated decision easy to follow.’ You did work carefully on the structure. You want to accept the praise without denying the effort, fishing for more, or taking credit for work others did.",
    tangledDraft:
      "Oh, it was nothing. Anyone could have done it.",
    draftDiagnosis:
      "The reply tries to sound modest but rejects both the compliment and the work behind it. A clean thank you can acknowledge the other person’s judgment without turning into self-promotion.",
    intention: "Accept the compliment and, if useful, acknowledge the real work that made the presentation clear.",
    invariant: "You receive the praise instead of denying it. You do not claim more credit than you earned or ask for further reassurance.",
    concept: "Receiving praise is a social skill. Gratitude can hold earned credit without either shrinking or swelling around it.",
    practiceCue: "Begin with thank you. Keep at most one sentence that names the real work or pleasure behind the result.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "Thank you. I worked hard to make it clear.",
        landing: "Plain receipt",
        protects: "The compliment and your effort are both allowed to stand.",
        tradeoff: "Naming the work can feel self-conscious if the exchange is very casual.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "Thank you. I’m glad the structure made it easier to follow.",
        landing: "Grateful and audience-aware",
        protects: "The praise is received while attention returns to the listener’s experience.",
        tradeoff: "The reply avoids naming how much effort the clarity required.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "Thank you. That means a great deal; I put real care into making it clear.",
        landing: "Warm and earned",
        protects: "The giver learns that the compliment reached you and named something intentional.",
        tradeoff: "That means a great deal may feel larger than an ordinary workplace compliment.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "Thank you. I’m pleased it landed clearly.",
        landing: "Quietly pleased",
        protects: "The compliment is accepted without a speech about the work.",
        tradeoff: "The formality may cool a warm personal exchange.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Thank you. Clarity was the work.",
        landing: "Compressed and assured",
        protects: "The sentence accepts credit for the exact thing the colleague noticed.",
        tradeoff: "Its polish can sound prepared rather than spontaneous.",
      },
    ],
  },
  {
    id: "concede-without-collapsing",
    contentVersion: 1,
    number: "20",
    title: "Concede without collapsing",
    provenance: "Original Verbalore",
    craft: ["concession", "argument structure", "evidence weighting"],
    size: "deep",
    minutes: 12,
    context: "A committee challenges the result of your pilot",
    skill: "Conceding a true weakness without surrendering a conclusion the evidence still supports",
    route: { intention: "hold-ground", relationship: "group", risk: "lost-meaning" },
    landingHazard: {
      label: "Concession can be mistaken for surrender or evasion",
      accidentalReading: "If you concede without restating the larger case, the room may think the proposal has failed. If you pivot too quickly, they may think you never accepted the criticism.",
      deliveryCue: "Give the conceded fact a complete sentence and a pause. Then show exactly why it changes the plan without erasing the remaining evidence.",
      saferAlternative: "I concede the onboarding miss. The support result still argues for expansion after we fix it.",
    },
    situation:
      "A committee member points out that your pilot’s onboarding took two days longer than forecast and argues that expansion should stop. They are right about the delay. After onboarding, support tickets fell 35 percent. You need to concede the miss, repair onboarding, and preserve the case for controlled expansion.",
    tangledDraft:
      "You’re right about onboarding, so maybe the whole proposal needs rethinking.",
    draftDiagnosis:
      "The sentence treats one valid criticism as if it settles the entire case. That is not openness; it is poor weighting of evidence. Concede the delay fully, then state what the support result still proves and what must change.",
    intention: "Concede the onboarding delay while maintaining the evidence-based case for expansion after the onboarding defect is fixed.",
    invariant: "Onboarding missed the forecast by two days. Support tickets still fell 35 percent. The plan must repair onboarding before a controlled expansion; neither fact disappears.",
    concept: "A real concession changes the argument. It does not require you to abandon every conclusion that survives it.",
    practiceCue: "Say the fact you concede without a but. Pause. Then name the evidence and action that remain.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "You’re right that onboarding ran two days late. That is a problem to fix, not a reason to discard the 35 percent drop in support tickets.",
        landing: "Conceded and bounded",
        protects: "The criticism is accepted while its evidential reach stays proportionate.",
        tradeoff: "Not a reason can sound argumentative before the room feels the concession has landed.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I concede the onboarding miss. I don’t concede the larger conclusion: support tickets fell 35 percent, so we should fix onboarding and continue a controlled expansion.",
        landing: "Respectful distinction",
        protects: "The listener’s valid point improves the plan without taking possession of the whole argument.",
        tradeoff: "Repeating concede can make the exchange sound like formal debate.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "That criticism improves the plan. Let’s repair onboarding and keep the benefit the pilot proved: 35 percent fewer support tickets.",
        landing: "Welcoming and directional",
        protects: "The challenge is treated as useful while the group receives a next step.",
        tradeoff: "The positive framing may seem to absorb the criticism too neatly.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "Onboarding missed the forecast. The support result still supports expansion after that defect is corrected.",
        landing: "Measured evidential balance",
        protects: "Both facts remain visible without turning the exchange into a contest.",
        tradeoff: "The wording does not acknowledge the committee member who surfaced the problem.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Fix the two days. Keep the thirty-five percent.",
        landing: "Sharp arithmetic",
        protects: "The correction and surviving benefit are memorable.",
        tradeoff: "The compression makes a complex rollout decision sound simpler than it is.",
      },
    ],
  },
  {
    id: "i-dont-know-yet",
    contentVersion: 1,
    number: "21",
    title: "I don’t know, yet",
    provenance: "Original Verbalore",
    craft: ["public uncertainty", "bounded certainty", "follow-through"],
    size: "quick",
    minutes: 4,
    context: "A public question before the final cost is known",
    skill: "Saying what is not yet known while naming what will make it knowable",
    route: { intention: "answer-pressure", relationship: "group", risk: "false-certainty" },
    landingHazard: {
      label: "Authority can tempt you to manufacture a number",
      accidentalReading: "A confident estimate may be repeated as fact, while a bare I don’t know may sound as if nobody owns the answer.",
      deliveryCue: "State the unknown without apology, then name the closing event and the exact time the confirmed answer will be published.",
      saferAlternative: "I don’t know the final cost yet. Procurement closes Friday, and I’ll publish the confirmed figure then.",
    },
    situation:
      "At a public question-and-answer session, a journalist asks for the final cost of a restoration project. Procurement is still open, so the final figure does not exist yet. It closes Friday, and you can publish the confirmed cost that afternoon. You must not turn an estimate into a fact.",
    tangledDraft:
      "It should be around four million, give or take.",
    draftDiagnosis:
      "The estimate sounds helpful, but in a public setting it can become tomorrow’s quoted final cost. The authoritative answer is not a guessed number; it is the boundary of current knowledge and a dependable route to the confirmed one.",
    intention: "Say that the final cost is not yet known and commit to publishing it after procurement closes Friday.",
    invariant: "The final cost is unknown today. Procurement closes Friday, and the confirmed figure will be published then. No estimate is presented as fact.",
    concept: "Authority is not the absence of uncertainty. It is precision about what is known, what is not, and what happens next.",
    practiceCue: "Give the unknown one clean sentence and the path to the answer one more. Do not fill the gap with a plausible guess.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "I don’t know the final cost yet. Procurement closes Friday, and I’ll publish the confirmed figure then.",
        landing: "Plain and accountable",
        protects: "The public receives an honest boundary and a named follow-through.",
        tradeoff: "The opening may be clipped into a headline without the second sentence.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I can’t give you a reliable final figure today. Procurement closes Friday; I’ll publish the confirmed cost that afternoon.",
        landing: "Careful and useful",
        protects: "The refusal is explained through reliability rather than secrecy.",
        tradeoff: "Can’t give may imply the figure exists and is being withheld.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "You deserve the confirmed number, not my guess. Procurement closes Friday, and I’ll publish the cost then.",
        landing: "Respectful candour",
        protects: "The audience’s right to accuracy carries the uncertainty.",
        tradeoff: "You deserve can sound rehearsed if the question is hostile.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "The final cost is not yet known. It will be published Friday after procurement closes.",
        landing: "Institutionally precise",
        protects: "The factual boundary and timetable stay free of personality or speculation.",
        tradeoff: "The passive wording hides who is accountable for publishing it.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Not known today. Confirmed Friday.",
        landing: "Hard-edged certainty about uncertainty",
        protects: "The limit and timetable are memorable.",
        tradeoff: "The fragments can sound evasive or irritated in a public room.",
      },
    ],
  },
  {
    id: "claim-the-work",
    contentVersion: 1,
    number: "22",
    title: "Claim the work",
    provenance: "Original Verbalore",
    craft: ["accurate credit", "visible confidence", "shared achievement"],
    size: "standard",
    minutes: 8,
    context: "A performance conversation about a recovered project",
    skill: "Naming your contribution precisely without erasing the team or making yourself smaller",
    route: { intention: "hold-ground", relationship: "person-you-need-to-move", risk: "self-erasure" },
    landingHazard: {
      label: "Claiming credit can swing from disappearance to ownership of everyone’s work",
      accidentalReading: "A vague team-only answer can erase your leadership, while an I-led-everything answer can appropriate the implementation others delivered.",
      deliveryCue: "Name your exact decisions and actions, then name the team’s distinct contribution without using either to cancel the other.",
      saferAlternative: "The team delivered it, and I led the recovery plan and the client calls.",
    },
    situation:
      "In a performance conversation, your manager describes a troubled project’s recovery as a team effort. That is true. You designed the recovery plan and led the client calls; teammates rebuilt and delivered the system. You need your contribution to be visible without claiming their work.",
    tangledDraft:
      "The team did everything, really. I was barely involved.",
    draftDiagnosis:
      "The reply sounds generous but makes the record false. It erases the leadership your manager is evaluating and leaves the team’s contribution no more accurate than a boast would. Separate the work clearly enough for both to be seen.",
    intention: "State that you designed the recovery plan and led the client calls while crediting the team for rebuilding and delivery.",
    invariant: "Your leadership contribution is explicit. The team retains credit for implementation and delivery. Neither side’s work is inflated or erased.",
    concept: "Accurate credit is not boasting. It is a truthful account of who carried which part of the result.",
    practiceCue: "Use one clause for your contribution and one for the team’s. Make both specific enough to survive a performance record.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "The team delivered it, and I led the recovery plan and the client calls.",
        landing: "Clear shared credit",
        protects: "Your leadership and the team’s delivery are both on the record.",
        tradeoff: "The sentence does not describe the team’s substantial rebuilding work.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "The team’s execution was excellent. My contribution was designing the recovery plan and leading the client calls.",
        landing: "Generous and exact",
        protects: "The team is credited before you identify your own distinct work.",
        tradeoff: "Leading with the team may let a hurried listener miss the evidence about you.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "I’m proud of what the team delivered. I set the recovery plan, led the client conversations, and they turned it into a working system.",
        landing: "Proud and inclusive",
        protects: "Leadership and implementation become parts of one shared achievement.",
        tradeoff: "They turned it into can imply a cleaner separation than collaborative work usually has.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "I led the recovery plan and client calls; the team carried the rebuild and delivery.",
        landing: "Measured division of work",
        protects: "The record is precise without praise or self-deprecation.",
        tradeoff: "The clean division can sound transactional in a conversation about shared success.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "I drew the recovery path. The team ran it.",
        landing: "Memorable leadership claim",
        protects: "Your strategic contribution and the team’s execution are easy to remember.",
        tradeoff: "The metaphor oversimplifies collaboration and may make the team sound merely operational.",
      },
    ],
  },
  {
    id: "a-no-that-reads-well",
    contentVersion: 1,
    number: "23",
    title: "A no that reads well",
    provenance: "Original Verbalore",
    craft: ["written refusal", "scope clarity", "useful alternatives"],
    size: "quick",
    minutes: 4,
    context: "A customer email asking for unplanned weekend work",
    skill: "Refusing a written request clearly without sounding punitive or quietly accepting it",
    route: { intention: "say-no", relationship: "person-you-need-to-move", risk: "hostility" },
    landingHazard: {
      label: "Written brevity can look colder than spoken brevity sounds",
      accidentalReading: "An efficient refusal has no voice, expression, or immediate repair around it. A customer may read a short boundary as punishment even when the scope decision is fair.",
      deliveryCue: "State what is unavailable, then offer only alternatives you can genuinely honour. Do not bury the no below a paragraph of appreciation.",
      saferAlternative: "Monday is not available within the current scope. I can schedule the added analysis for Thursday or check a separately priced rush option.",
    },
    situation:
      "At five on Friday, a customer emails asking for additional analysis by Monday. The work is outside the current scope. You can deliver it on Thursday within an agreed extension, or check whether a separately priced rush is possible. You cannot promise the weekend work for free.",
    tangledDraft:
      "Thanks for sending this. We’ll do our best to get something over by Monday.",
    draftDiagnosis:
      "The email sounds helpful but accepts a deadline and scope you cannot honour. Do our best transfers the eventual disappointment into Monday. Refuse the unavailable option now and present only real alternatives.",
    intention: "Decline the Monday delivery within the current scope and offer Thursday or a separately priced rush check.",
    invariant: "Monday is not available within the current scope. Thursday is available through an agreed extension, and a rush option is only possible after a separate check and price.",
    concept: "A written no earns trust when the unavailable path closes early and every offered alternative is real.",
    practiceCue: "Put the unavailable option in the first two sentences. Delete appreciation that delays the answer and options you cannot guarantee.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "We can’t deliver the additional analysis by Monday within the current scope. The earliest available delivery is Thursday.",
        landing: "Clear written boundary",
        protects: "The customer can plan from a real date instead of a false attempt.",
        tradeoff: "It omits the possible rush route and can feel closed.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "The additional analysis is outside the current scope, so Monday is not available as an included delivery. I can schedule it for Thursday or price a separate rush request.",
        landing: "Firm with real options",
        protects: "The no stays intact while the customer receives two honest paths.",
        tradeoff: "Included delivery may sound contractual rather than relational.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "I want to give you work we can stand behind. We can deliver the added analysis on Thursday within scope, or I can check whether a separately priced rush is possible.",
        landing: "Service-minded refusal",
        protects: "Quality and helpful alternatives carry the boundary.",
        tradeoff: "The Monday refusal remains implied and should be explicit earlier in a long email thread.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "The current scope does not include the additional analysis. Thursday is the earliest available delivery.",
        landing: "Formal and contained",
        protects: "The scope fact and available date remain easy to quote and forward.",
        tradeoff: "The formality may feel severe in a long-standing customer relationship.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Monday is outside scope. Thursday is available.",
        landing: "Fast written contrast",
        protects: "The unavailable and available paths cannot be confused.",
        tradeoff: "The fragments can read as dismissive when no spoken warmth accompanies them.",
      },
    ],
  },
  {
    id: "say-it-another-way",
    contentVersion: 1,
    number: "24",
    title: "Say it another way",
    provenance: "Original Verbalore",
    craft: ["clarification", "cross-cultural respect", "repair without blame"],
    size: "standard",
    minutes: 7,
    context: "An international video call with an unfamiliar expression",
    skill: "Restoring understanding without blaming accent, intelligence, or fluency",
    route: { intention: "restore-understanding", relationship: "work-peer", risk: "humiliation" },
    landingHazard: {
      label: "A request for clarity can become a judgment on the speaker",
      accidentalReading: "Words such as normal, clear English, or your accent move the problem from one unfamiliar expression to the speaker’s identity or ability.",
      deliveryCue: "Own the gap as yours, identify the expression, and ask for the same point in different words. Do not imitate the speaker or apologise at length.",
      saferAlternative: "I don’t know that expression. Could you say the same point another way?",
    },
    situation:
      "During an international video call, a colleague uses an idiom you do not know while explaining the final decision. The audio is clear and the problem is the unfamiliar expression, not their accent or intelligence. You need the decision in different words before you act on it.",
    tangledDraft:
      "Sorry, your English is difficult for me. Can you say that normally?",
    draftDiagnosis:
      "The sentence turns one unknown idiom into a judgment about the colleague’s English and suggests that their way of speaking is abnormal. Own the missing knowledge, name the expression, and ask for a rephrasing of the point.",
    intention: "Ask the colleague to express the decision another way so you understand it accurately.",
    invariant: "You do not understand one expression and need the same decision rephrased. The request does not blame accent, intelligence, fluency, or culture.",
    concept: "Repair is shared work, but blame is optional. A precise request for different words protects both understanding and dignity.",
    practiceCue: "Use I for the gap, point to the expression rather than the person, and ask for the same meaning in another form.",
    stances: [
      {
        id: "direct",
        label: "Direct",
        line: "I don’t know that expression. Could you say the same point another way?",
        landing: "Clear and owned",
        protects: "The exact gap and required repair are easy to understand.",
        tradeoff: "The interruption can feel abrupt if the speaker has not finished the thought.",
      },
      {
        id: "tactful",
        label: "Tactful",
        line: "I may be missing the idiom. Could you rephrase the decision in plain words?",
        landing: "Careful clarification",
        protects: "The request distinguishes one expression from the speaker’s overall English.",
        tradeoff: "Plain words may imply that the original wording was needlessly difficult.",
      },
      {
        id: "warm-authority",
        label: "Warm authority",
        line: "I want to make sure I carry your point correctly. Could you say that expression another way?",
        landing: "Respectful and responsible",
        protects: "Accuracy and respect for the speaker become the reason to pause.",
        tradeoff: "The warm framing adds length during a fast-moving call.",
      },
      {
        id: "restrained",
        label: "Restrained",
        line: "Could you rephrase that expression for me?",
        landing: "Light and unobtrusive",
        protects: "The conversation resumes quickly without any judgment of the speaker.",
        tradeoff: "It does not confirm that the decision itself, rather than only the phrase, must be understood.",
      },
      {
        id: "punchy",
        label: "Punchy",
        line: "Same point, different words?",
        landing: "Quick repair",
        protects: "The requested action is compact enough for a live interruption.",
        tradeoff: "The fragment can sound impatient or commanding without a warm voice.",
      },
    ],
  },
];
